/**
 * [PORTABLE] Upload Modal for the Onboarding Feature - Gamified & Glassmorphism Design
 * Target path in main project: src/features/onboarding/components/SelfieUploadModal.tsx
 */
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Upload, X, Sparkles } from "lucide-react";
import mixpanel from "mixpanel-browser";

import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

import { useOnboardingUiStore } from "../store/onboarding-ui-store";
import { useUploadSelfie } from "../hooks/use-onboarding-mutations";
import { useOnboarding } from "../hooks/use-onboarding";
import { SELFIE_LEVELS } from "../data/selfie-grid";

export function SelfieUploadModal() {
  const { t } = useTranslation();
  const isOpen = useOnboardingUiStore((s) => s.isUploadModalOpen);
  const closeModal = useOnboardingUiStore((s) => s.closeUploadModal);
  const upload = useUploadSelfie();
  const inputRef = useRef<HTMLInputElement>(null);
  
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { progress, creditsData } = useOnboarding();

  const handleFile = (file: File) => {
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleClose = () => {
    if (!upload.isPending) {
      setPreview(null);
      setSelectedFile(null);
      closeModal();
    }
  };

  if (!isOpen) return null;

  const completedCount = Math.min(
    SELFIE_LEVELS,
    progress?.selfie?.levels?.filter(
      (l) => (l.isCompleted || l.status === "completed") && l.levelId !== "S0"
    ).length || 0
  );
  
  const progressPercentage = Math.round((completedCount / SELFIE_LEVELS) * 100);
  const creditsRemaining = creditsData?.usage?.tokens?.subscription ?? 0;
  const creditsTotal = creditsData?.usage?.maxTokens?.subscription ?? 3000;

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-md animate-in fade-in duration-300">
      
      <div className="bg-bg-primary w-full max-w-200 h-full sm:h-auto sm:max-h-[90dvh] sm:rounded-4xl shadow-2xl border-x sm:border border-border-primary relative flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
        
        {/* MODAL HEADER (Mor/Fuşya Tema ve Sade Emoji) */}
        <div className="shrink-0 p-6 border-b border-border-primary flex items-center justify-between bg-bg-primary z-20 shadow-[0_4px_20px_-10px_rgba(0,0,0,0.05)]">
          <div className="flex items-center gap-4">
            {/* 🚀 DÜZELTME: Emoji arka planı silindi, sade ve doğal bırakıldı */}
            <div className="flex items-center justify-center text-3xl sm:text-4xl pr-1">
              📸
            </div>
            <div>
              <h2 className="text-xl font-extrabold text-text-primary leading-tight">
                L0 — {t("onboarding.levels.S0.label", { defaultValue: "Selfie'niz" })}
              </h2>
              {/* 🚀 DÜZELTME: Mavi yerine Mor (purple) kullanıldı */}
              <p className="text-[10px] uppercase tracking-widest font-bold text-purple-600 dark:text-purple-400 mt-0.5">
                {t("onboarding.levels.S0.focus", { defaultValue: "YÜKLEME" })}
              </p>
            </div>
          </div>
          {/* Header'daki X butonu yerine sade bir çarpı eklendi */}
          <button onClick={handleClose} className="p-2 rounded-full hover:bg-bg-secondary transition-colors text-text-tertiary hover:text-text-primary">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* KAYDIRILABİLİR İÇERİK */}
        <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-8 pb-32 sm:pb-8 bg-bg-primary/50">
          
          <div className="rounded-2xl bg-bg-tertiary/50 border border-border-primary p-4 shadow-inner min-h-20 flex items-center">
            <p className="w-full text-sm text-text-primary leading-relaxed italic text-center">
              "{t("onboarding.levels.S0.prompt", { defaultValue: "AI dönüşüm yolculuğunuza başlamak için bir selfie yükleyin." })}"
            </p>
          </div>

          {/* Upload Alanı (Mor glow efektleriyle) */}
          <div
            onClick={() => !upload.isPending && inputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const file = e.dataTransfer.files?.[0];
              if (file && file.type.startsWith("image/")) handleFile(file);
            }}
            className={cn(
              "rounded-4xl border-2 transition-all p-10 text-center cursor-pointer min-h-65 flex items-center justify-center relative overflow-hidden",
              selectedFile 
                ? "border-purple-500 border-dashed bg-purple-500/5 shadow-[0_0_30px_rgba(168,85,247,0.15)]" 
                : "border-border-primary border-dashed bg-bg-tertiary/40 hover:bg-bg-tertiary/60"
            )}
          >
            {preview ? (
              <img
                src={preview}
                alt="preview"
                className="relative z-10 w-48 h-48 rounded-4xl object-cover border-4 border-white dark:border-gray-800 shadow-[0_0_40px_-10px_rgba(0,0,0,0.3)] dark:shadow-[0_0_40px_-10px_rgba(255,255,255,0.1)] transition-transform duration-500 hover:scale-105"
              />
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="p-4 rounded-full bg-bg-primary shadow-sm border border-border-primary">
                  <Upload className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
                <p className="text-sm font-medium text-text-primary">{t("onboarding.dropOrClick")}</p>
              </div>
            )}
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFile(file);
              }}
            />
          </div>

          {/* KREDİ VE İLERLEME (Mor Gradient Tasarım) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-bg-secondary p-5 rounded-2xl flex items-center justify-between border border-border-primary shadow-sm">
              <div>
                <p className="text-[10px] font-bold text-text-tertiary uppercase tracking-widest">{t("onboarding.creditsRemaining", { defaultValue: "Kalan Kredi" })}</p>
                <p className="text-lg font-extrabold text-text-primary mt-0.5">
                  {creditsRemaining} <span className="text-sm font-normal text-text-tertiary">/ {creditsTotal}</span>
                </p>
              </div>
              <div className="w-10 h-10 bg-linear-to-br from-fuchsia-500/10 to-purple-600/10 rounded-full flex items-center justify-center text-purple-600 dark:text-purple-400">
                <Sparkles className="w-5 h-5" />
              </div>
            </div>

            <div className="bg-bg-secondary p-5 rounded-2xl flex flex-col justify-center border border-border-primary shadow-sm">
              <div className="flex justify-between items-center mb-2">
                <p className="text-[10px] font-bold text-text-tertiary uppercase tracking-widest">{t("onboarding.progress", { defaultValue: "İlerleme" })}</p>
                <span className="text-xs font-black text-purple-600 dark:text-purple-400">%{progressPercentage}</span>
              </div>
              <Progress 
                value={progressPercentage} 
                className="h-2.5 bg-bg-tertiary [&>div]:bg-linear-to-r [&>div]:from-fuchsia-500 [&>div]:to-purple-600 shadow-[0_0_8px_rgba(168,85,247,0.3)]" 
              />
            </div>
          </div>

        </div>

        {/* MODAL FOOTER */}
        <div className="shrink-0 p-6 bg-bg-secondary/50 border-t border-border-primary flex items-center justify-between gap-4 z-20">
          
          <button 
            onClick={handleClose} 
            disabled={upload.isPending}
            className="px-6 py-3.5 text-sm font-bold text-text-tertiary hover:text-text-primary transition-colors disabled:opacity-50"
          >
            {t("onboarding.close", { defaultValue: "Kapat" })}
          </button>
          
          <Button
            variant="darkPrimary"
            onClick={() => {
              if (preview && selectedFile) {
                try { mixpanel.track("onboarding_action_started", { action_type: "upload", level_id: "S0" }); } catch (e) {}
                upload.mutate(selectedFile, { onSuccess: () => handleClose() });
              } else {
                inputRef.current?.click();
              }
            }}
            disabled={upload.isPending || !preview}
            className="px-8 py-7 rounded-full font-bold text-sm shadow-xl shadow-purple-500/20 flex items-center gap-3 group transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            <Upload className="h-5 w-5 transform group-hover:-translate-y-1 transition-transform" />
            {upload.isPending ? t("onboarding.uploading") : t("onboarding.uploadSelfie", { defaultValue: "Selfie Yükle" })}
          </Button>
        </div>

      </div>
    </div>
  );
}